import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useLabProgress } from '../../hooks/useLabProgress';

import {
  ArrowLeft, Star, Check, Lock, ChevronRight, RotateCcw, Eye, EyeOff,
  Scissors, Layers, BookOpen
} from 'lucide-react';

// ── Specimen Images ─────────────────────────────────────────────────
const IMAGES = {
  frog_external: 'https://static.prod-images.emergentagent.com/jobs/f072039a-19c4-425b-b071-367212e38fcd/images/b48308574b2215d8dfc997b2005542ec23327bd3f893c2aa74ff4ea194e9ed75.png',
  frog_internal: 'https://static.prod-images.emergentagent.com/jobs/f072039a-19c4-425b-b071-367212e38fcd/images/782292c7443642f240cdb43cf7369727481747d29885b2f380975cd9887c60ca.png',
  rat_external: 'https://static.prod-images.emergentagent.com/jobs/f072039a-19c4-425b-b071-367212e38fcd/images/120c2dc165f56fe13488c5b795ba0c761a23565ea2319414f2f176c86bf690ee.png',
  rat_internal: 'https://static.prod-images.emergentagent.com/jobs/f072039a-19c4-425b-b071-367212e38fcd/images/273f359e37bcbbbc89fc8b595a5db8450030e4eeeeecd4d53908ccf3d77465b0.png',
};

const getSpecimenImage = (specimen, targetLayer) => {
  if (specimen === 'frog') {
    return (targetLayer === 'skin') ? IMAGES.frog_external : IMAGES.frog_internal;
  }
  if (specimen === 'rat') {
    return (targetLayer === 'fur') ? IMAGES.rat_external : IMAGES.rat_internal;
  }
  return IMAGES.frog_internal;
};

// ── Mission Data ────────────────────────────────────────────────────
const MISSIONS = [
  {
    id: 1, title: 'Frog: External Anatomy', difficulty: 1, specimen: 'frog',
    description: 'Identify the external features of a frog by placing labels on the correct spots.',
    targetLayer: 'skin',
    organs: [
      { id: 'eye', name: 'Bulging Eye', x: 58, y: 19, desc: 'Large, protruding eyes for wide field of vision; can retract to help swallow food', color: '#3b82f6' },
      { id: 'nostril', name: 'Nostril', x: 48, y: 15, desc: 'External nares for breathing air when above water', color: '#a78bfa' },
      { id: 'tympanum', name: 'Tympanum', x: 63, y: 24, desc: 'Eardrum behind each eye — frogs hear through this membrane', color: '#22c55e' },
      { id: 'forelimb', name: 'Forelimb', x: 37, y: 38, desc: '4 digits, shorter than hind limbs — used for landing after jumps', color: '#f59e0b' },
      { id: 'hindlimb', name: 'Hind Limb', x: 63, y: 74, desc: '5 webbed digits — powerful muscles for jumping and swimming', color: '#ec4899' },
      { id: 'cloaca', name: 'Cloaca', x: 50, y: 84, desc: 'Common opening for digestive, urinary, and reproductive systems', color: '#ef4444' },
    ],
  },
  {
    id: 2, title: 'Frog: Muscular Layer', difficulty: 1, specimen: 'frog',
    description: 'Identify the major muscles visible after removing the skin.',
    targetLayer: 'muscle',
    organs: [
      { id: 'pectoralis', name: 'Pectoralis', x: 42, y: 36, desc: 'Chest muscle controlling forelimb movement', color: '#ef4444' },
      { id: 'rectus-abd', name: 'Rectus Abdominis', x: 50, y: 50, desc: 'Abdominal muscle for body flexion', color: '#f87171' },
      { id: 'gastrocnemius', name: 'Gastrocnemius', x: 63, y: 70, desc: 'Powerful calf muscle in hind leg — enables jumping', color: '#dc2626' },
      { id: 'dorsalis', name: 'Dorsalis Scapulae', x: 38, y: 28, desc: 'Shoulder muscle that moves the forelimb backward', color: '#b91c1c' },
    ],
  },
  {
    id: 3, title: 'Frog: Digestive System', difficulty: 2, specimen: 'frog',
    description: 'Label the digestive organs visible after opening the body cavity.',
    targetLayer: 'organs',
    organs: [
      { id: 'liver', name: 'Liver', x: 42, y: 30, desc: 'Large, 3-lobed organ producing bile for fat digestion', color: '#92400e' },
      { id: 'stomach', name: 'Stomach', x: 55, y: 36, desc: 'J-shaped organ for chemical digestion with acid', color: '#f59e0b' },
      { id: 'sm-intestine', name: 'Small Intestine', x: 50, y: 55, desc: 'Coiled tube for nutrient absorption', color: '#fbbf24' },
      { id: 'lg-intestine', name: 'Large Intestine', x: 60, y: 62, desc: 'Short, straight tube for water absorption', color: '#d97706' },
      { id: 'gall-bladder', name: 'Gall Bladder', x: 38, y: 38, desc: 'Small green sac storing bile from the liver', color: '#16a34a' },
    ],
  },
  {
    id: 4, title: 'Frog: Heart & Circulation', difficulty: 2, specimen: 'frog',
    description: 'Identify the unique 3-chambered heart and circulatory organs.',
    targetLayer: 'organs',
    organs: [
      { id: 'heart', name: '3-Chambered Heart', x: 45, y: 26, desc: '2 atria + 1 ventricle — mixes oxygenated and deoxygenated blood', color: '#ef4444' },
      { id: 'lungs', name: 'Lungs', x: 35, y: 30, desc: 'Simple, sac-like lungs — frog also breathes through skin!', color: '#ec4899' },
      { id: 'spleen', name: 'Spleen', x: 58, y: 44, desc: 'Filters blood and recycles old red blood cells', color: '#7c3aed' },
      { id: 'kidney', name: 'Kidneys', x: 62, y: 56, desc: 'Elongated organs filtering waste from blood to make urine', color: '#dc2626' },
    ],
  },
  {
    id: 5, title: 'Frog: Nervous System', difficulty: 2, specimen: 'frog',
    description: 'Explore the brain and spinal cord of the frog.',
    targetLayer: 'organs',
    organs: [
      { id: 'cerebrum', name: 'Cerebrum', x: 48, y: 12, desc: 'Controls voluntary actions — relatively small in frogs', color: '#a78bfa' },
      { id: 'optic-lobe', name: 'Optic Lobes', x: 55, y: 15, desc: 'Large lobes for vision processing — frogs rely heavily on sight', color: '#8b5cf6' },
      { id: 'cerebellum', name: 'Cerebellum', x: 58, y: 18, desc: 'Controls balance and coordination of movement', color: '#7c3aed' },
      { id: 'medulla', name: 'Medulla Oblongata', x: 54, y: 22, desc: 'Controls involuntary functions: heartbeat, breathing, digestion', color: '#6d28d9' },
      { id: 'spinal-cord', name: 'Spinal Cord', x: 50, y: 40, desc: 'Extends through vertebral column — relays signals between brain and body', color: '#c4b5fd' },
    ],
  },
  {
    id: 6, title: 'Rat: External Features', difficulty: 1, specimen: 'rat',
    description: 'Identify the external features of a laboratory rat.',
    targetLayer: 'fur',
    organs: [
      { id: 'vibrissae', name: 'Vibrissae (Whiskers)', x: 18, y: 38, desc: 'Sensory whiskers for spatial awareness in dark environments', color: '#78716c' },
      { id: 'pinna', name: 'Pinna (Ear)', x: 38, y: 22, desc: 'Large external ear for acute hearing — rats hear ultrasound', color: '#a8a29e' },
      { id: 'tail', name: 'Tail', x: 82, y: 62, desc: 'Long, scaly tail for balance and temperature regulation', color: '#d6d3d1' },
      { id: 'forepaw', name: 'Forepaw', x: 28, y: 68, desc: '5 digits with claws — used for gripping and grooming', color: '#f5f5f4' },
      { id: 'hindpaw', name: 'Hind Paw', x: 68, y: 72, desc: '5 digits — larger than forepaws for powerful locomotion', color: '#e7e5e4' },
    ],
  },
  {
    id: 7, title: 'Rat: Muscular System', difficulty: 2, specimen: 'rat',
    description: 'Identify the major muscles of the rat.',
    targetLayer: 'muscle',
    organs: [
      { id: 'masseter', name: 'Masseter', x: 28, y: 34, desc: 'Powerful jaw muscle — rats gnaw through almost anything', color: '#ef4444' },
      { id: 'trapezius', name: 'Trapezius', x: 45, y: 30, desc: 'Upper back muscle controlling head and shoulder movement', color: '#dc2626' },
      { id: 'biceps', name: 'Biceps Brachii', x: 30, y: 50, desc: 'Upper forelimb flexor muscle', color: '#f87171' },
      { id: 'quad', name: 'Quadriceps', x: 65, y: 55, desc: 'Large thigh muscle group for running and jumping', color: '#b91c1c' },
      { id: 'diaphragm', name: 'Diaphragm', x: 48, y: 44, desc: 'Dome-shaped muscle separating thorax from abdomen — essential for breathing', color: '#fca5a5' },
    ],
  },
  {
    id: 8, title: 'Rat: Vital Organs', difficulty: 2, specimen: 'rat',
    description: 'Explore and label the vital internal organs of the rat.',
    targetLayer: 'organs',
    organs: [
      { id: 'heart-4', name: '4-Chambered Heart', x: 42, y: 28, desc: '2 atria + 2 ventricles — complete separation of oxygenated and deoxygenated blood', color: '#ef4444' },
      { id: 'lungs-r', name: 'Lungs', x: 38, y: 22, desc: 'Lobed lungs with millions of alveoli for efficient gas exchange', color: '#ec4899' },
      { id: 'liver-r', name: 'Liver', x: 45, y: 40, desc: 'Multi-lobed organ — detoxifies blood, produces bile. Note: rats lack a gall bladder!', color: '#92400e' },
      { id: 'kidney-r', name: 'Kidneys', x: 58, y: 50, desc: 'Bean-shaped organs filtering blood — rats produce concentrated urine', color: '#dc2626' },
      { id: 'intestines-r', name: 'Intestines', x: 50, y: 60, desc: 'Long, coiled small intestine for nutrient absorption + caecum for plant digestion', color: '#f59e0b' },
    ],
  },
  {
    id: 9, title: 'Rat: Brain Anatomy', difficulty: 3, specimen: 'rat',
    description: 'Study the rat brain and label its regions.',
    targetLayer: 'organs',
    organs: [
      { id: 'cerebrum-r', name: 'Cerebrum', x: 30, y: 16, desc: 'Largest brain region — smooth surface (unlike human wrinkled cortex)', color: '#a78bfa' },
      { id: 'cerebellum-r', name: 'Cerebellum', x: 42, y: 20, desc: 'Coordinates motor functions — relatively large for agile movement', color: '#8b5cf6' },
      { id: 'olfactory-r', name: 'Olfactory Bulbs', x: 20, y: 28, desc: 'Very large — rats have an incredible sense of smell (300+ million receptors)', color: '#c084fc' },
      { id: 'hypothalamus-r', name: 'Hypothalamus', x: 36, y: 26, desc: 'Controls hunger, thirst, body temperature, and circadian rhythms', color: '#7c3aed' },
      { id: 'brainstem-r', name: 'Brain Stem', x: 48, y: 30, desc: 'Controls vital involuntary functions: breathing, heart rate, blood pressure', color: '#6d28d9' },
    ],
  },
  {
    id: 10, title: 'Compare: Frog vs Rat', difficulty: 3, specimen: 'both',
    description: 'Compare the key anatomical differences between frog and rat.',
    targetLayer: 'organs',
    organs: [
      { id: 'comp-heart', name: 'Heart Chambers', x: 25, y: 30, desc: 'Frog: 3 chambers (mixed blood) | Rat: 4 chambers (complete separation)', color: '#ef4444' },
      { id: 'comp-skin', name: 'Skin Function', x: 75, y: 30, desc: 'Frog: Moist skin for gas exchange | Rat: Fur-covered, no respiratory role', color: '#22c55e' },
      { id: 'comp-temp', name: 'Body Temperature', x: 25, y: 60, desc: 'Frog: Ectothermic (cold-blooded) | Rat: Endothermic (warm-blooded, ~37 C)', color: '#3b82f6' },
      { id: 'comp-limbs', name: 'Limb Structure', x: 75, y: 60, desc: 'Frog: Webbed hind feet for swimming | Rat: Clawed paws for gripping', color: '#f59e0b' },
      { id: 'comp-repro', name: 'Reproduction', x: 50, y: 80, desc: 'Frog: External fertilization in water | Rat: Internal fertilization, placental', color: '#ec4899' },
    ],
  },
];

// ── Organ Spot (Click to place) ─────────────────────────────────────
function OrganSpot({ organ, isRevealed, selectedLabel, onPlace }) {
  const canPlace = selectedLabel === organ.id;
  return (
    <div className="absolute transform -translate-x-1/2 -translate-y-1/2"
      style={{ left: `${organ.x}%`, top: `${organ.y}%`, zIndex: isRevealed ? 20 : 10 }}
      data-testid={`spot-${organ.id}`}>
      {isRevealed ? (
        <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 300 }}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border-2 bg-black/70 backdrop-blur-sm whitespace-nowrap shadow-lg"
          style={{ borderColor: organ.color }}>
          <Check className="w-3 h-3 flex-shrink-0" style={{ color: organ.color }} />
          <span className="text-[11px] font-bold" style={{ color: organ.color }}>{organ.name}</span>
        </motion.div>
      ) : (
        <motion.button
          onClick={() => onPlace(organ.id)}
          animate={canPlace ? { scale: [1, 1.4, 1], borderColor: '#22c55e' } : { scale: [1, 1.2, 1] }}
          transition={{ duration: canPlace ? 0.5 : 2, repeat: Infinity }}
          className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${
            canPlace
              ? 'bg-emerald-500/40 border-emerald-400 cursor-pointer shadow-lg shadow-emerald-500/30'
              : 'bg-muted border-white/40 hover:bg-muted hover:border-primary cursor-pointer'
          }`}
          title={canPlace ? `Place ${organ.name} here` : 'Select a label first, then click here'}
          data-testid={`place-${organ.id}`}>
          <span className="text-[8px] font-bold text-muted-foreground">?</span>
        </motion.button>
      )}
    </div>
  );
}

// ── Specimen View with Real Images ──────────────────────────────────
function SpecimenView({ mission, revealedOrgans, selectedLabel, onPlace }) {
  const isBoth = mission.specimen === 'both';

  return (
    <div className="relative w-full rounded-2xl overflow-hidden border border-border bg-muted dark:bg-[#0a0d16]"
      style={{ aspectRatio: isBoth ? '16/9' : '1/1', maxHeight: isBoth ? 380 : 460 }}
      data-testid="specimen-view">
      {/* Grid background */}
      <div className="absolute inset-0 opacity-[0.06]"
        style={{ backgroundImage: 'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      {isBoth ? (
        <div className="absolute inset-0 flex">
          <div className="flex-1 relative border-r border-border">
            <div className="absolute top-3 left-3 z-20 px-2 py-1 rounded-md bg-emerald-500/20 border border-emerald-500/30">
              <span className="text-[10px] font-bold text-emerald-400">FROG</span>
            </div>
            <img src={IMAGES.frog_internal} alt="Frog anatomy" className="w-full h-full object-contain p-4" loading="lazy" />
          </div>
          <div className="flex-1 relative">
            <div className="absolute top-3 left-3 z-20 px-2 py-1 rounded-md bg-amber-500/20 border border-amber-500/30">
              <span className="text-[10px] font-bold text-amber-400">RAT</span>
            </div>
            <img src={IMAGES.rat_internal} alt="Rat anatomy" className="w-full h-full object-contain p-4" loading="lazy" />
          </div>
        </div>
      ) : (
        <img
          src={getSpecimenImage(mission.specimen, mission.targetLayer)}
          alt={`${mission.specimen} anatomy`}
          className="w-full h-full object-contain p-6"
          loading="lazy"
        />
      )}

      {/* Organ spots */}
      {mission.organs.map(organ => (
        <OrganSpot key={organ.id} organ={organ}
          isRevealed={revealedOrgans.includes(organ.id)}
          selectedLabel={selectedLabel}
          onPlace={onPlace} />
      ))}

      {/* Specimen label */}
      {!isBoth && (
        <div className="absolute top-3 left-3 z-20 px-2 py-1 rounded-md bg-muted border border-border">
          <span className="text-[10px] font-bold text-muted-foreground uppercase">{mission.specimen} - {mission.targetLayer}</span>
        </div>
      )}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────
export default function VirtualDissectionLab() {
  const navigate = useNavigate();
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission } = useLabProgress('virtual-dissection', MISSIONS.length);
  const [revealedOrgans, setRevealedOrgans] = useState([]);
  const [showMissions, setShowMissions] = useState(true);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [showInfo, setShowInfo] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [wrongSpot, setWrongSpot] = useState(null);
  const [score, setScore] = useState(0);

  const mission = MISSIONS[currentMission];

  const checkCompletion = useCallback((newRevealed) => {
    if (newRevealed.length === mission.organs.length && !completedMissions.includes(currentMission)) {
      completeMission(currentMission);
      setScore(prev => prev + mission.difficulty * 15);
    }
  }, [mission, currentMission, completedMissions, completeMission]);

  const handleSelectLabel = (organId) => {
    if (revealedOrgans.includes(organId)) return;
    setSelectedLabel(prev => prev === organId ? null : organId);
  };

  const handlePlace = (spotId) => {
    if (!selectedLabel) return;
    if (selectedLabel === spotId) {
      // Correct placement!
      const newRevealed = [...revealedOrgans, spotId];
      setRevealedOrgans(newRevealed);
      const organ = mission.organs.find(o => o.id === spotId);
      setShowInfo(organ);
      setSelectedLabel(null);
      checkCompletion(newRevealed);
    } else {
      // Wrong spot
      setWrongSpot(spotId);
      setTimeout(() => setWrongSpot(null), 1200);
    }
  };

  const handleShowAllAnswers = () => {
    setShowAnswer(prev => !prev);
    if (!showAnswer) {
      const allIds = mission.organs.map(o => o.id);
      setRevealedOrgans(allIds);
      checkCompletion(allIds);
    }
  };

  const selectMission = (idx) => {
    if (!unlockedMissions.includes(idx)) return;
    setCurrentMission(idx);
    setShowMissions(false);
    setRevealedOrgans([]);
    setSelectedLabel(null);
    setShowInfo(null);
    setShowAnswer(false);
    setWrongSpot(null);
  };

  const resetCurrentMission = () => {
    setRevealedOrgans([]);
    setSelectedLabel(null);
    setShowInfo(null);
    setShowAnswer(false);
    setWrongSpot(null);
  };

  // ── Mission Selector ──
  if (showMissions) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/labs')} className="text-foreground/80 hover:text-foreground hover:bg-muted" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-red-600 to-rose-800 flex items-center justify-center shadow-lg shadow-red-500/30">
                  <Scissors className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-base font-bold">Virtual Dissection Lab</h1>
                  <p className="text-[10px] text-muted-foreground">Frog & Rat Anatomy</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-amber-400 border-amber-400/30 bg-amber-400/10">
                <Star className="w-3 h-3 mr-1 fill-amber-400" />{score} pts
              </Badge>
              <Badge variant="outline" className="text-emerald-400 border-emerald-400/30 bg-emerald-400/10">
                {completedMissions.length}/{MISSIONS.length}
              </Badge>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2 text-foreground">Choose Your Dissection</h2>
            <p className="text-foreground/60 text-sm">Select a label, then click the matching spot on the specimen to identify organs.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="mission-grid">
            {MISSIONS.map((m, idx) => {
              const unlocked = unlockedMissions.includes(idx);
              const completed = completedMissions.includes(idx);
              return (
                <motion.button key={m.id} onClick={() => selectMission(idx)} disabled={!unlocked}
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
                  className={`p-5 rounded-2xl border text-left transition-all group ${
                    completed ? 'border-emerald-500/40 bg-emerald-500/5' : unlocked ? 'border-border bg-card hover:bg-muted hover:border-border' : 'border-border/50 bg-card opacity-70 cursor-not-allowed'
                  }`} data-testid={`mission-${idx}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${completed ? 'bg-emerald-500 text-white' : unlocked ? 'bg-muted' : 'bg-muted/50 text-muted-foreground/60'}`}>
                        {completed ? <Check className="w-4 h-4" /> : unlocked ? idx + 1 : <Lock className="w-3 h-3" />}
                      </span>
                      <h3 className="font-semibold text-base text-foreground">{m.title}</h3>
                    </div>
                    <div className="flex gap-0.5">
                      {[...Array(m.difficulty)].map((_, i) => <Star key={i} className={`w-3 h-3 ${completed ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/30'}`} />)}
                    </div>
                  </div>
                  <p className="text-sm text-foreground/80 mb-2">{m.description}</p>
                  <div className="flex items-center gap-1.5 text-xs text-foreground/60">
                    {m.specimen === 'frog' && <span className="text-emerald-400">Frog</span>}
                    {m.specimen === 'rat' && <span className="text-amber-400">Rat</span>}
                    {m.specimen === 'both' && <span className="text-violet-400">Both</span>}
                    <span className="mx-1">|</span>
                    <span>{m.organs.length} organs to find</span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </main>
      </div>
    );
  }

  const isComplete = revealedOrgans.length === mission.organs.length;

  // ── Lab Bench ──
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="dissection-bench">
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setShowMissions(true)} className="text-foreground/80 hover:text-foreground hover:bg-muted" data-testid="back-to-missions">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-sm font-bold">Mission {currentMission + 1}: {mission.title}</h1>
              <p className="text-[10px] text-muted-foreground/70">{mission.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-muted-foreground border-border">
              {revealedOrgans.length}/{mission.organs.length} found
            </Badge>
            <Badge variant="outline" className="text-amber-400 border-amber-400/30 bg-amber-400/10">
              <Star className="w-3 h-3 mr-1 fill-amber-400" />{score}
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
          <div className="space-y-4">
            {/* Specimen View with real images */}
            <SpecimenView
              mission={mission}
              revealedOrgans={revealedOrgans}
              selectedLabel={selectedLabel}
              onPlace={handlePlace}
            />

            {/* Label Tray - Click to select, then click on specimen spot */}
            <div className="p-4 rounded-2xl border border-border bg-card" data-testid="label-tray">
              <div className="flex items-center gap-2 mb-3">
                <Layers className="w-4 h-4 text-rose-400" />
                <h3 className="text-sm font-semibold">Select a Label, Then Click the Matching Spot</h3>
                <span className="text-[10px] text-muted-foreground/70 ml-auto">{revealedOrgans.length}/{mission.organs.length}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {mission.organs.map(organ => {
                  const isPlaced = revealedOrgans.includes(organ.id);
                  const isSelected = selectedLabel === organ.id;
                  return (
                    <motion.button key={organ.id}
                      onClick={() => handleSelectLabel(organ.id)}
                      whileHover={!isPlaced ? { scale: 1.05 } : {}}
                      whileTap={!isPlaced ? { scale: 0.95 } : {}}
                      className={`px-3 py-2 rounded-lg text-xs font-medium border-2 transition-all select-none ${
                        isPlaced
                          ? 'opacity-40 cursor-default border-border bg-muted/50 line-through text-muted-foreground/60'
                          : isSelected
                            ? 'cursor-pointer border-emerald-400 bg-emerald-400/15 shadow-lg shadow-emerald-500/20 ring-1 ring-emerald-400/30'
                            : 'cursor-pointer hover:bg-muted border-border hover:border-white/40'
                      }`}
                      style={!isPlaced && !isSelected ? { borderColor: organ.color + '50', color: organ.color } : isSelected ? { color: '#22c55e' } : {}}
                      disabled={isPlaced}
                      data-testid={`label-${organ.id}`}>
                      {isSelected && <span className="mr-1">&#x2794;</span>}
                      {organ.name}
                    </motion.button>
                  );
                })}
              </div>
              {selectedLabel && (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="mt-2 text-[11px] text-emerald-400/80 font-medium">
                  Now click the matching pulsing spot on the image above to place it
                </motion.p>
              )}
            </div>

            {/* Info panel for last revealed organ */}
            <AnimatePresence>
              {showInfo && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="p-4 rounded-2xl border bg-card" style={{ borderColor: showInfo.color + '40' }}
                  data-testid="organ-info">
                  <p className="text-xs font-bold mb-1" style={{ color: showInfo.color }}>{showInfo.name}</p>
                  <p className="text-sm text-foreground/80 leading-relaxed">{showInfo.desc}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Wrong spot feedback */}
            <AnimatePresence>
              {wrongSpot && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="p-3 rounded-xl border border-red-500/30 bg-red-500/10 text-red-400 text-sm text-center">
                  Wrong spot! Try a different location on the specimen.
                </motion.div>
              )}
            </AnimatePresence>

            {/* Show Answer panel */}
            {showAnswer && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="p-5 rounded-2xl border border-blue-500/30 bg-blue-500/5"
                data-testid="answer-panel">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="w-4 h-4 text-blue-400" />
                  <h3 className="text-sm font-bold text-blue-400">All Organs & Descriptions</h3>
                </div>
                <div className="space-y-2">
                  {mission.organs.map(organ => (
                    <div key={organ.id} className="p-2.5 rounded-lg bg-card border border-white/5">
                      <p className="text-xs font-bold" style={{ color: organ.color }}>{organ.name}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{organ.desc}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Completion */}
            {isComplete && !showAnswer && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="p-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 text-center" data-testid="mission-complete">
                <p className="text-emerald-400 font-bold text-lg mb-2">All Organs Identified!</p>
                <p className="text-muted-foreground text-sm mb-4">You successfully labeled all {mission.organs.length} organs.</p>
                <div className="flex gap-3 justify-center">
                  <Button onClick={resetCurrentMission}
                    variant="outline" className="border-border text-foreground/80 hover:text-foreground hover:bg-muted">
                    <RotateCcw className="w-4 h-4 mr-1.5" />Redo
                  </Button>
                  {currentMission < MISSIONS.length - 1 && (
                    <Button onClick={() => selectMission(currentMission + 1)}
                      className="bg-gradient-to-r from-violet-600 to-indigo-600" data-testid="next-mission-btn">
                      Next Dissection <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              </motion.div>
            )}
          </div>

          {/* Right Panel */}
          <div className="space-y-4 lg:sticky lg:top-[65px] lg:self-start">
            {/* Action buttons */}
            <div className="space-y-2">
              <Button onClick={handleShowAllAnswers}
                variant="outline" className="w-full border-blue-500/30 text-blue-400 hover:bg-blue-500/10 hover:text-blue-300"
                data-testid="show-answer-btn">
                {showAnswer ? <><EyeOff className="w-4 h-4 mr-2" /> Hide Answers</> : <><Eye className="w-4 h-4 mr-2" /> Show Answers</>}
              </Button>
              <Button onClick={resetCurrentMission}
                variant="outline" className="w-full border-border text-muted-foreground hover:text-foreground hover:bg-muted"
                data-testid="reset-btn">
                <RotateCcw className="w-4 h-4 mr-2" /> Reset Mission
              </Button>
            </div>

            {/* How to play */}
            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2 flex items-center gap-1.5">
                <Scissors className="w-4 h-4 text-rose-400" />How to Dissect
              </h3>
              <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
                <li>Click a label from the tray below the image</li>
                <li>The selected label turns green</li>
                <li>Click the matching pulsing spot on the specimen</li>
                <li>Read the description to learn about each organ!</li>
                <li>Or use "Show Answers" to reveal everything</li>
              </ol>
            </div>

            {/* Mission progress sidebar */}
            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2">All Missions</h3>
              <div className="space-y-1.5">
                {MISSIONS.map((m, idx) => (
                  <button key={m.id} onClick={() => unlockedMissions.includes(idx) && selectMission(idx)}
                    disabled={!unlockedMissions.includes(idx)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all ${
                      idx === currentMission ? 'bg-primary text-primary-foreground' :
                      completedMissions.includes(idx) ? 'text-emerald-400 hover:bg-muted/50' :
                      unlockedMissions.includes(idx) ? 'text-muted-foreground hover:bg-muted/50' : 'text-muted-foreground/30 cursor-not-allowed'
                    }`} data-testid={`sidebar-mission-${idx}`}>
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold ${
                      completedMissions.includes(idx) ? 'bg-emerald-500 text-white' : idx === currentMission ? 'bg-muted' : 'bg-muted/50'
                    }`}>
                      {completedMissions.includes(idx) ? <Check className="w-3 h-3" /> : unlockedMissions.includes(idx) ? idx + 1 : <Lock className="w-2.5 h-2.5" />}
                    </span>
                    <span className="truncate">{m.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
